import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Separator } from "@replo/design-system/components/separator/Separator";
import Input from "@v1/components/common/designSystem/Input";
import ErrorMessage from "@v1/components/common/ErrorMessage/ErrorMessage";
import ColorSavedStyleGroup from "@v1/components/designLibrary/ColorSavedStyleGroup";
import TextSavedStyleGroup from "@v1/components/designLibrary/TextSavedStyleGroup";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { normalizeFontFamily } from "@v1/components/editor/page/element-editor/components/modifiers/utils";
import useShopStyles from "@v1/hooks/designLibrary/useGetDesignLibrarySavedStyles";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import { aiClient } from "@v1/trpc/routes/ai";
import { ConnectShopifyCallout } from "@v2/components/ConnectShopifyCallout";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { CheckCircle, Sparkles } from "lucide-react";
import { useForm } from "react-hook-form";
import { animated, config, useTransition } from "react-spring";
import { urlFormSchema } from "schemas/url";
import { v4 as uuidv4 } from "uuid";

import PresetShopStylePreviewCards from "./PresetShopStylePreviewCards";
import ShopStylesPreviewCard from "./ShopStylesPreviewCard";
import { useDraftStyles } from "./useDraftStyles";

type FormValues = {
  url: string;
};

const ShopStylesEditor: React.FC = () => {
  const logEvent = useLogAnalytics();
  const [isImportingStyles, setIsImportingStyles] = React.useState(false);
  const [viewState, setViewState] = React.useState<"standard" | "pullFromUrl">(
    "standard",
  );
  const { project } = useCurrentProjectContext();
  const projectId = project?.id;
  const errorToast = useV1ErrorToast();
  const workspaceId = useCurrentWorkspaceId();

  const {
    draftStyles,
    isLoading,
    setDraftStyles,
    areDraftStylesDifferentFromShopStyles,
    persistDraftStyles,
    applyPresetStyles,
    createDraftTextStyle,
    updateDraftTextStyle,
    deleteDraftTextStyle,
    createDraftColorStyle,
    updateDraftColorStyle,
    deleteDraftColorStyle,
  } = useDraftStyles();

  const projectHasShopifyIntegration = Boolean(project?.integrations?.shopify);

  const { colorShopStyles, textShopStyles } = useShopStyles();

  const handleCancel = () => {
    setDraftStyles({
      text: textShopStyles || [],
      color: colorShopStyles || [],
    });
  };

  const hasSavedStyles =
    draftStyles !== null &&
    (draftStyles.text.length > 0 || draftStyles.color.length > 0);

  // Find the highest level heading style and paragraph style
  const { highestHeadingStyle, paragraphStyle } = React.useMemo(() => {
    const headingPriority = ["h1", "h2", "h3", "h4", "h5", "h6"];
    let highestHeadingStyle = undefined;
    let paragraphStyle = undefined;

    const stylesToUse = draftStyles?.text || textShopStyles || [];

    if (stylesToUse.length > 0) {
      for (const tag of headingPriority) {
        const foundStyle = stylesToUse.find(
          (style) => style.type === "text" && style.attributes?.htmlTag === tag,
        );

        if (foundStyle) {
          highestHeadingStyle = foundStyle;
          break;
        }
      }

      paragraphStyle = stylesToUse.find(
        (style) => style.type === "text" && style.attributes?.htmlTag === "p",
      );
    }

    return {
      highestHeadingStyle,
      paragraphStyle,
    };
  }, [draftStyles, textShopStyles]);

  async function handleUrlSubmit(url: string) {
    setIsImportingStyles(true);

    try {
      const { color: colorStyles, text: textStyles } =
        await aiClient.generatePrimaryStyles.query({
          url,
          projectId,
        });

      setDraftStyles({
        text:
          textStyles?.map((style) => ({
            ...style,
            id: uuidv4(),
            attributes: {
              ...style.attributes,
              fontFamily: normalizeFontFamily(
                style.attributes.fontFamily ?? null,
              ),
              lineHeight:
                style.attributes.lineHeight ||
                (() => {
                  const fontSize = style.attributes.fontSize;
                  if (!fontSize) {
                    return undefined;
                  }

                  const fontSizeValue = Number.parseInt(
                    fontSize.replace(/\D/g, ""),
                  );
                  if (isNaN(fontSizeValue)) {
                    return undefined;
                  }

                  return `${fontSizeValue + 4}px`;
                })(),
            },
          })) || [],
        color: colorStyles?.map((style) => ({ ...style, id: uuidv4() })) || [],
      });
    } catch {
      errorToast(
        "Failed to generate styles",
        "Please try again, or contact support@replo.app for help.",
        "error.ai.generatePrimaryStyles",
        {},
      );
    }
    setViewState("standard");
    setIsImportingStyles(false);
  }

  const displayColorStyles = draftStyles?.color ?? colorShopStyles;

  const showIntegrateWithShopifyCard =
    !projectHasShopifyIntegration && !hasSavedStyles;

  const showPresetStyleList =
    !hasSavedStyles && !isImportingStyles && viewState === "standard";

  const showPullFromUrlView =
    !hasSavedStyles && !isImportingStyles && viewState === "pullFromUrl";

  const showSaveChanges =
    !isImportingStyles &&
    viewState === "standard" &&
    (hasSavedStyles ||
      (showPresetStyleList && areDraftStylesDifferentFromShopStyles));

  if (isLoading) {
    return null;
  }

  return (
    <div className="relative flex h-full w-full flex-col gap-3">
      {showIntegrateWithShopifyCard && (
        <>
          <div className="border-0.5 border-border flex h-64 w-full flex-col justify-between rounded-md">
            <div className="flex flex-col p-3">
              <h3 className="text-xs font-semibold pb-0.5">
                Integrate with Shopify
              </h3>
              <p className="text-muted mb-4 text-xs">
                We'll automatically pull your theme, products, and store info.
              </p>

              <ConnectShopifyCallout
                cookieValue={{
                  type: "shopStyles",
                  workspaceId: workspaceId!,
                  projectId: projectId!,
                }}
                suppressContent={true}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Separator />
            <span className="text-muted text-xs">or</span>
            <Separator />
          </div>
        </>
      )}
      {hasSavedStyles && (
        <>
          <ShopStylesPreviewCard
            headingTextStyle={
              highestHeadingStyle ?? {
                type: "text",
                name: "Default Header",
                attributes: {
                  type: "text",
                  htmlTag: "p",
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontWeight: "400",
                  color: "#000000",
                },
                optionalUsageGuidelines: null,
                isPrimary: false,
              }
            }
            bodyTextStyle={
              paragraphStyle ?? {
                type: "text",
                name: "Default Paragraph",
                attributes: {
                  type: "text",
                  htmlTag: "p",
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontWeight: "400",
                  color: "#000000",
                },
                optionalUsageGuidelines: null,
                isPrimary: false,
              }
            }
            colorStyles={displayColorStyles ?? []}
          />
          <Separator />
        </>
      )}

      {isImportingStyles && (
        <PullFromUrlLoadingState isLoading={isImportingStyles} />
      )}

      {hasSavedStyles && (
        <div className="pr-1">
          <TextSavedStyleGroup
            highestHeadingStyle={highestHeadingStyle}
            paragraphStyle={paragraphStyle}
            isLoading={isLoading}
            createDraftTextStyle={createDraftTextStyle}
            updateDraftTextStyle={updateDraftTextStyle}
            deleteDraftTextStyle={deleteDraftTextStyle}
            draftStyles={draftStyles}
          />
          <ColorSavedStyleGroup
            isLoading={isLoading}
            createDraftColorStyle={createDraftColorStyle}
            updateDraftColorStyle={updateDraftColorStyle}
            deleteDraftColorStyle={deleteDraftColorStyle}
            draftStyles={draftStyles}
          />
        </div>
      )}

      {showPresetStyleList && (
        <>
          <div className="flex w-full flex-row items-center justify-between">
            <div className="text-xs font-semibold">Start with a preset</div>
            <DeprecatedButton
              variant="link"
              onClick={() => setViewState("pullFromUrl")}
            >
              Pull from URL
            </DeprecatedButton>
          </div>
          <PresetShopStylePreviewCards
            isLoading={isLoading}
            applyPresetStyles={applyPresetStyles}
          />
        </>
      )}
      {showPullFromUrlView && (
        <PullFromUrlForm
          onCancel={() => setViewState("standard")}
          onSubmit={(url) => {
            setIsImportingStyles(true);
            void handleUrlSubmit(url);
          }}
        />
      )}
      {showSaveChanges && (
        <div className="sticky bottom-0 z-50 w-full bg-white">
          <Separator className="mb-4" />
          <div className="mb-3 flex flex-row gap-3">
            <DeprecatedButton
              size="sm"
              variant="primary"
              onClick={() => {
                logEvent("shopDetails.styles.save");
                void persistDraftStyles();
              }}
              isLoading={isLoading}
              disabled={!areDraftStylesDifferentFromShopStyles}
            >
              Save
            </DeprecatedButton>
            <DeprecatedButton
              size="sm"
              variant="secondary"
              onClick={() => {
                logEvent("shopDetails.styles.cancel");
                handleCancel();
              }}
              disabled={!areDraftStylesDifferentFromShopStyles}
            >
              Cancel
            </DeprecatedButton>
          </div>
        </div>
      )}
    </div>
  );
};

const PullFromUrlLoadingState: React.FC<{ isLoading: boolean }> = ({
  isLoading,
}) => {
  const { project } = useCurrentProjectContext();
  const projectName = project?.name;

  const timeoutsRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);
  const [content, setContent] = React.useState<string[]>([
    "Analyzing text styles...",
  ]);
  const transitions = useTransition(content, {
    from: { opacity: 0, innerHeight: 0 },
    enter: { opacity: 1, innerHeight: 28 },
    config: config.stiff,
  });

  React.useEffect(() => {
    // NOTE (Fran 2024-12-20): This is a multistage animation that runs when the loading state is triggered.
    // It's based on a React Spring example https://codesandbox.io/s/k467x.
    // The timings are not based on any real data, but they are designed to be
    // long enough to be noticeable, but not too long. Since the AI generation styles takes time,
    // we want to show the user that something is happening.
    if (isLoading) {
      const initLoadingAnimation = () => {
        timeoutsRef.current.forEach(clearTimeout);
        timeoutsRef.current = [];
        timeoutsRef.current.push(
          setTimeout(
            () => setContent((prev) => [...prev, "Parsing color palette..."]),
            5000,
          ),
        );
        timeoutsRef.current.push(
          setTimeout(() => {
            return setContent((prev) => [
              ...prev,
              "Naming and tagging your styles...",
            ]);
          }, 10_000),
        );
        timeoutsRef.current.push(
          setTimeout(() => {
            return setContent((prev) => [
              ...prev,
              projectName
                ? `Preparing styles for ${projectName}...`
                : "Preparing styles...",
            ]);
          }, 15_000),
        );
      };
      initLoadingAnimation();
    }
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [isLoading, projectName]);

  return (
    <div className="border-0.5 border-border flex h-40 flex-col items-center justify-center gap-2 rounded-md p-4 transition-all">
      {transitions(({ innerHeight, ...styles }, item, _, index) => {
        const isLast = index === content.length - 1;
        return (
          <animated.div
            style={styles}
            className="text-default flex w-full items-center gap-2"
          >
            <animated.div
              style={{ maxHeight: innerHeight }}
              className="flex gap-2"
            >
              {!isLast ? (
                <CheckCircle size={16} className="text-primary shrink-0" />
              ) : (
                <Sparkles size={16} className="text-primary shrink-0" />
              )}
              <span className="text-xs">{item}</span>
            </animated.div>
          </animated.div>
        );
      })}
    </div>
  );
};

const PullFromUrlForm: React.FC<{
  onCancel: () => void;
  onSubmit: (url: string) => void;
}> = ({ onCancel, onSubmit }) => {
  const logEvent = useLogAnalytics();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onSubmit",
    defaultValues: {
      url: "",
    },
    resolver: zodResolver(urlFormSchema),
  });

  const urlError = errors.url?.message;
  const [isLoading, setIsLoading] = React.useState(false);

  const onSubmitForm = async ({ url }: FormValues) => {
    setIsLoading(true);
    logEvent("library.style.import", {
      importMethod: "url",
    });
    onSubmit(url);
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit(onSubmitForm)(e);
      }}
    >
      <div className="flex w-full flex-row items-center justify-between">
        <div className="text-xs font-semibold">Enter URL</div>
        <DeprecatedButton variant="link" onClick={onCancel}>
          Show Presets
        </DeprecatedButton>
      </div>
      <div className="flex flex-col gap-2">
        <Input
          autoComplete="off"
          placeholder="https://www.mywebsite.com"
          {...register("url")}
          type="text"
          size="base"
          validityState={Boolean(urlError) ? "invalid" : "valid"}
          autoFocus
        />
        {urlError && <ErrorMessage id="error-url" error={urlError} />}
      </div>
      <div className="flex flex-row items-center justify-end">
        <DeprecatedButton
          type="submit"
          variant="primary"
          isLoading={isLoading}
          disabled={isLoading}
          size="base"
        >
          Pull from URL
        </DeprecatedButton>
      </div>
    </form>
  );
};

export default ShopStylesEditor;
